import { useEffect } from "react";
import { useRouter } from "next/router";
import { useAtomValue, useSetAtom } from "jotai";
import { altDataUpgradeDialogState, userDataState, userPayloadSettledState } from "@/lib/store";
import { AltDataUserData, AltDataView, altDataView, hasAltDataAccess } from "@/lib/altDataAccess";

export { altDataView, hasAltDataAccess };
export type { AltDataUserData, AltDataView };

/**
 * Access control for the Alt Data section.
 *
 * Alt Data is part of ORTEX Advanced and above (Advanced, Professional,
 * Institutional). The backend decides (`has_advanced_platform`) and puts the
 * answer on the `/API/v2/user` payload as `alt_data`; the menu shows the entry
 * to everyone and unentitled users get the upgrade dialog.
 *
 * This deliberately **fails closed**, which is the opposite default to
 * {@link useOptionsUiRedirect}. That hook guards pages a user is normally
 * entitled to see, so an unknown answer keeps them where they are; this one
 * guards a page nobody is entitled to by default, so an unknown answer must
 * not reveal it. Concretely, a backend that predates the flag omits
 * `alt_data`, and the section stays hidden rather than opening to everyone.
 *
 * The API enforces the same entitlement independently
 * (`interface.api.altdata` requires `PRODUCT_ALT_DATA`), so this is about
 * not showing a dead page — not the security boundary.
 */

/** {@link hasAltDataAccess} for the current user: `undefined` until the user call has settled. */
export function useAltDataAccess(): boolean | undefined {
    const userData = useAtomValue(userDataState) as AltDataUserData | null | undefined;
    const settled = useAtomValue(userPayloadSettledState);
    return hasAltDataAccess(userData, settled);
}

/** {@link altDataView} for the current user. */
export function useAltDataView(): AltDataView {
    const userData = useAtomValue(userDataState) as AltDataUserData | null | undefined;
    const settled = useAtomValue(userPayloadSettledState);
    return altDataView(userData, settled);
}

/**
 * Should the Alt Data page render its content?
 *
 * False until the entitlement is confirmed, so an unentitled user never sees
 * the section flash before the redirect lands.
 */
export function useAltDataVisible(): boolean {
    return useAltDataAccess() === true;
}

/**
 * Send users without the entitlement away from an Alt Data route and show
 * them the upgrade dialog instead of a dead page.
 *
 * Only acts once the payload has arrived and the answer is a definite "no" —
 * an undecidable state redirects nobody, it just renders nothing (see
 * {@link useAltDataVisible}), so a slow payload can never bounce an entitled
 * user off their own page.
 */
export function useAltDataRedirect(destination = "/") {
    const access = useAltDataAccess();
    const setUpgradeOpen = useSetAtom(altDataUpgradeDialogState);
    const router = useRouter();

    useEffect(() => {
        if (access === false) {
            // Opened once the redirect has landed: the dialog closes on any later navigation.
            router.replace(destination).then(() => setUpgradeOpen(true));
        }
        // router identity churns per render; path-relevant inputs only.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [access, destination]);
}
